import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import Topbar from '../../components/topbar';
import DynamicAdminTable, { Column } from '../../components/admin-table';
import EditSubscriptionTierModal from '../../components/popup/edit-subscription-tier';
import CreateSubscriptionPlanModal, { NewPlanForm } from '../../components/popup/create-subscription-plan';
import DeletePopup from '../../components/popup/delete';
import { useToast } from '../../providers/toaster-provider';
import { AddonConfig, TierConfig, useSubscriptionTiers, INCLUSIONS, inclusionMode, formatStorage, DEFAULT_STORAGE_MB } from '../../util/subscription-tiers';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';

// Backed by the real SubscriptionTiers table - an edit here changes what
// every subscriber actually sees at checkout and what the backend guards
// enforce, not just a local preview. See util/subscription-tiers.ts.
const SubscriptionTiersAdmin: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { tiers, refetch } = useSubscriptionTiers();
    const [editingTier, setEditingTier] = useState<TierConfig | null>(null);
    const [updateApi, setUpdateApi] = useApi();
    const [resetApi, setResetApi] = useApi();
    const [createApi, setCreateApi] = useApi();
    const [deleteApi, setDeleteApi] = useApi();
    const [creating, setCreating] = useState(false);
    const [deletingTier, setDeletingTier] = useState<TierConfig | null>(null);
    // The add-on catalog, for each tier's "Add-ons this plan can buy".
    const [addonsApi, setAddonsApi] = useApi<{ success: boolean; addons: AddonConfig[] }>();
    const catalog = addonsApi.data?.addons || [];

    useEffect(() => { document.title = 'FineAnts Admin | Subscription Tiers'; }, []);

    useEffect(() => {
        setAddonsApi(buildApiRequestConfig(routes.subscriptionTiers.addons));
    }, [setAddonsApi]);

    useEffect(() => {
        const { data, isLoading, error } = updateApi;
        if (data && !isLoading) {
            showSuccess('Saved');
            refetch();
            setAddonsApi(buildApiRequestConfig(routes.subscriptionTiers.addons));
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save tier');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateApi]);

    useEffect(() => {
        const { data, isLoading, error } = resetApi;
        if (data && !isLoading) {
            showSuccess('Reset to defaults');
            refetch();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to reset tiers');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetApi]);

    useEffect(() => {
        const { data, isLoading, error } = createApi;
        if (data && !isLoading) {
            showSuccess('Plan added');
            setCreating(false);
            refetch();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to add plan');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createApi]);

    useEffect(() => {
        const { data, isLoading, error } = deleteApi;
        if (data && !isLoading) {
            showSuccess('Deleted');
            setDeletingTier(null);
            refetch();
        }
        if (error) {
            // e.g. companies are still on it - the message says so.
            showError(typeof error === 'string' ? error : 'Failed to delete');
            setDeletingTier(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteApi]);

    const handleCreate = (plan: NewPlanForm) => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.create);
        setCreateApi({ url, method, body: plan });
    };

    const handleDelete = () => {
        if (!deletingTier) return;
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.delete, [{ param: 'id', value: deletingTier.id }]);
        setDeleteApi({ url, method });
    };

    const handleToggle = (id: string, field: keyof TierConfig, value: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.update, [{ param: 'id', value: id }]);
        setUpdateApi({ url, method, body: { [field]: value } });
    };

    const handleSaveEdit = (updated: TierConfig, newAddonPrices: Record<string, number> = {}) => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.update, [{ param: 'id', value: updated.id }]);
        setUpdateApi({
            url, method, body: {
                // Name and display order are shared by the plan's Monthly and
                // Yearly tiers (the server keeps the other one in step).
                ...(updated.id !== 'free' ? { name: updated.name } : {}),
                sortOrder: updated.sortOrder ?? 0,
                billingNote: updated.billingNote,
                pricePerMonth: updated.pricePerMonth,
                minimumGuaranteed: updated.minimumGuaranteed,
                horizonDays: updated.horizonDays,
                users: updated.users,
                storageMb: updated.storageMb,
                // Included in the plan - add-on prices live in Admin >
                // Subscription Add-ons.
                ...Object.fromEntries(INCLUSIONS.map(i => [i.key, !!updated[i.key]])),
                availableAddons: updated.availableAddons || [],
                // Prices typed for add-ons that weren't offered yet - offers them.
                newAddonPrices,
            },
        });
        setEditingTier(null);
    };

    const handleReset = () => {
        const { url, method } = buildApiRequestConfig(routes.subscriptionTiers.reset);
        setResetApi({ url, method });
    };

    const rows = tiers.map(t => ({
        ...t,
        displayName: t.billing ? `${t.name} - ${t.billing === 'annual' ? '12mo up front' : t.billing === 'daily' ? 'Daily (QA)' : 'No commitment'}` : t.name,
        isVisible: t.isVisible !== false,
        sortOrder: t.sortOrder ?? 0,
        storageDisplay: formatStorage(t.storageMb ?? DEFAULT_STORAGE_MB),
        pricePerMonthDisplay: `${t.pricePerMonth.toLocaleString()}`,
        minimumGuaranteedDisplay: `$${t.minimumGuaranteed.toLocaleString()}`,
        // "Included", "Add-on" (bought separately - Admin > Subscription
        // Add-ons) or "Off". Inclusions are edited in the modal.
        ...Object.fromEntries(INCLUSIONS.map(({ key }) => {
            const mode = inclusionMode(t, key);
            const display = mode === 'included' ? 'Included' : mode === 'addon' ? 'Add-on' : 'Off';
            return [`${key}Display`, display];
        })),
    }));

    const columns: Column[] = [
        { id: 'displayName', label: 'Tier', size: 200, disabled: true },
        { id: 'sortOrder', label: 'Order', size: 70, disabled: true },
        { id: 'pricePerMonthDisplay', label: 'Price/mo', size: 90, disabled: true },
        { id: 'minimumGuaranteedDisplay', label: 'Charged Now', size: 100, disabled: true },
        { id: 'horizonDays', label: 'Horizon (d)', size: 90, disabled: true },
        { id: 'users', label: 'Users', size: 70, disabled: true },
        { id: 'storageDisplay', label: 'Storage', size: 90, disabled: true },
        { id: 'userAclDisplay', label: 'User ACL', size: 100, disabled: true },
        { id: 'xeroIntegrationDisplay', label: 'Xero', size: 100, disabled: true },
        { id: 'stripeIntegrationDisplay', label: 'Stripe', size: 100, disabled: true },
        { id: 'advancedFeaturesDisplay', label: 'Advanced', size: 100, disabled: true },
        { id: 'documentsDisplay', label: 'Documents', size: 100, disabled: true },
        {
            id: 'highlight', label: 'Most Popular', type: 'toggle', size: 110,
            onToggle: (row, val) => handleToggle(row.id, 'highlight', val),
        },
        {
            // Hidden = not offered in the plan dialog; companies on it keep it.
            id: 'isVisible', label: 'Visible', type: 'toggle', size: 80,
            onToggle: (row, val) => handleToggle(row.id, 'isVisible', val),
        },
        {
            id: 'actions', label: 'Actions', type: 'actions', size: 80,
            onEdit: (row) => setEditingTier(tiers.find(t => t.id === row.id) ?? null),
            onDelete: (row) => setDeletingTier(tiers.find(t => t.id === row.id) ?? null),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <EditSubscriptionTierModal
                open={!!editingTier}
                tier={editingTier}
                addons={catalog}
                onClose={() => setEditingTier(null)}
                onSave={handleSaveEdit}
            />
            <CreateSubscriptionPlanModal
                open={creating}
                saving={createApi.isLoading}
                onClose={() => setCreating(false)}
                onCreate={handleCreate}
            />
            <DeletePopup
                open={!!deletingTier}
                red
                message={`Delete ${deletingTier?.name ?? ''}${deletingTier?.billing ? ` (${deletingTier.billing === 'annual' ? 'Yearly' : 'Monthly'})` : ''}?`}
                description="Only a plan no company is on can be deleted. To stop offering it but keep it for current companies, turn off Visible instead."
                confirmLabel="Delete"
                loading={deleteApi.isLoading}
                onConfirm={handleDelete}
                onCancel={() => setDeletingTier(null)}
            />
            <Topbar title="Subscription Tiers" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setCreating(true)}
                        sx={{ textTransform: 'none', background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', fontWeight: 600 }}
                    >
                        Add plan
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleReset}
                        sx={{ textTransform: 'none', color: 'var(--color-text-muted)', borderColor: 'var(--color-text-muted)' }}
                    >
                        Reset to defaults
                    </Button>
                </Box>
                <DynamicAdminTable columns={columns} data={rows} />
            </div>
        </div>
    );
};

export default SubscriptionTiersAdmin;
